/**
 * sanitize.js  --  one-stop HTML sanitisation for the ARP frontend.
 *
 * Every place that renders user-influenced or PDF-derived HTML (via
 * dangerouslySetInnerHTML, .innerHTML, or document.write) MUST run the
 * content through `sanitizeHtml()` first.  This neutralises any <script>,
 * <iframe>, on*-handler, javascript:-url, or other XSS vector while
 * preserving the editorial markup the document layout depends on
 * (headings, paragraphs, lists, figures, strong, em, etc.).
 */
import DOMPurify from 'dompurify';

// Tags the editorial document layout relies on.  Everything else is stripped.
const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'col', 'colgroup',
  'dd', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'mark', 'ol', 'p', 'pre', 'q',
  's', 'section', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody',
  'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
];

const ALLOWED_ATTR = [
  'class', 'id', 'href', 'target', 'rel', 'src', 'alt', 'loading',
  'data-diagram-slug', 'data-testid', 'colspan', 'rowspan', 'title',
];

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'oninput', 'onsubmit'],
  ALLOW_DATA_ATTR: false,
};

// Force every <a> through a hook that adds rel="noopener noreferrer" and
// strips any javascript:/data:/vbscript: URLs.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    if (/^\s*(javascript|data|vbscript):/i.test(href)) {
      node.removeAttribute('href');
    }
    if (node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

/**
 * Sanitise an HTML string for safe rendering.  Returns a plain string
 * suitable for both dangerouslySetInnerHTML AND .innerHTML assignment.
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

/**
 * Helper for the `dangerouslySetInnerHTML` prop:  returns the React-shaped
 * `{ __html }` object with sanitised content in one call.
 *
 *   <div dangerouslySetInnerHTML={safeHtml(reading.content)} />
 */
export function safeHtml(html) {
  return { __html: sanitizeHtml(html) };
}
